/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { isRegisterPortConnectionAckFailedMessage, isRegisterPortConnectionAckMessage, REGISTER_PORT_CONNECTION_ACK_FAILED, isPortConnectionComplete } from '../messages/FromWorkerMessageTypes';
import { createRegisterPortConnectionMessage, createUnRegisterPortConnectionMessage } from '../messages/ToWorkerMessages';
import { ConnectionStatusTypes } from '../types/ConnectionStatuses';
import { GENERIC_WORKER_ERROR, connectSharedWorkerPort } from './utils/connectSharedWorkerPort';
import { createVersionBroadcastChannel } from '../utils/createVersionBroadcastChannel';
import { makeChannelName } from '../utils/makeChannelName';
import { isMessageReceived, sendMessageAndAck } from '../utils/sendMessageAndAck';
import { createWorkerMessage } from './utils/createWorkerMessage';
import MessageChannelTypes from '../constants/MessageChannelTypes';
import { createBroadcastMessageChannels } from '../utils/createBroadcastMessageChannels';
import MessageDuplicateManager from '../utils/MessageDuplicateManager';
import defer from 'hs-promise-utils/defer';
import { isVersionDeprecationMessage } from '../messages/VersionBroadcastChannelMessageTypes';
export class SharedWorkerMessagingClient {
  constructor(options) {
    this.connectionStatus = ConnectionStatusTypes.UNINITIALIZED;
    this.DISCONNECTED_REGISTRATION_ERROR_MESSAGE = 'Registration interrupted by disconnect';
    this._versionBroadcastChannel = null;
    this._sharedWorker = null;
    this._outboundBroadcastChannel = null;
    this._broadcastChannels = null;
    this._handleConnectionStatusUpdate = (status, data) => {
      this.connectionStatus = status;
      this._onVersionWorkerStatusUpdate(status, data);
    };
    this._sendPortRegistration = () => {
      if (this._debug) this._debug(`Registering connection with SharedWorker`);
      if (this.connectionStatus === ConnectionStatusTypes.DISCONNECTING) {
        return Promise.reject(new Error(this.DISCONNECTED_REGISTRATION_ERROR_MESSAGE));
      }
      const message = createRegisterPortConnectionMessage(Object.assign({
        name: this._name,
        originConnectionId: this._connectionId,
        appDataVersion: this._appDataVersion,
        portalId: this._portalId
      }, this._getAdditionalRegistrationInformation()));
      return sendMessageAndAck({
        port: this._sharedWorker.port,
        errorAckMessageType: REGISTER_PORT_CONNECTION_ACK_FAILED,
        message,
        timeout: 10000
      });
    };
    this._handleSharedWorkerInitialized = result => {
      if (this.connectionStatus === ConnectionStatusTypes.DISCONNECTING) {
        throw new Error(this.DISCONNECTED_REGISTRATION_ERROR_MESSAGE);
      } else if (!result.messageEvent || !result.messageEvent.data || !isMessageReceived(result) || !isRegisterPortConnectionAckMessage(result.messageEvent.data)) {
        let message = result.errorMessage || `${result.responseMessageType} never recieved for an unknown reason, status ${result.status}.`;
        let cause;
        const messageEventData = result.messageEvent && result.messageEvent.data;
        if (messageEventData && isRegisterPortConnectionAckFailedMessage(messageEventData)) {
          message = `${result.responseMessageType} never recieved, ${messageEventData.payload.message}`;
          cause = messageEventData.payload.stack;
        }
        throw new Error(message, {
          cause
        });
      }
      const {
        data
      } = result.messageEvent;
      if (this._debug) this._debug(`SharedWorker registration succeeded`);
      return data.payload;
    };
    this._messageDuplicateManager = new MessageDuplicateManager();
    this._handleMessage = (channelType, message) => {
      const messageData = message && message.data || {};
      if (isPortConnectionComplete(messageData)) {
        clearTimeout(this._portConnectedDeferredTimeout);
        this._portConnectedDeferredTimeout = undefined;
        this._portConnectedDeferred.resolve(undefined);
        return;
      }
      const messageHasId = messageData.id;
      const messageDestinationMatch = !messageData.destinationConnectionId || messageData.destinationConnectionId === this._connectionId;
      if (!messageHasId || !messageDestinationMatch || this._messageDuplicateManager.wasMessageIdRecieved(message.data.id, channelType)) {
        return;
      }
      this._onMessage(message, channelType);
    };
    this._handleVersionBroadcastChannelMessage = messageEvent => {
      if (!messageEvent.data || !isVersionDeprecationMessage(messageEvent.data) || !messageEvent.data.payload) {
        return;
      }
      const {
        data: {
          payload
        }
      } = messageEvent;
      const portaIdMatch = payload.portalId === this._portalId;
      const isCurrentWorkerVersion = payload.workerVersion === this._clientVersion;
      if (!this._sharedWorker || !this._versionBroadcastChannel || !payload.workerVersion || !portaIdMatch || isCurrentWorkerVersion) {
        return;
      }
      if (this._debug) this._debug(`Recieved version deprecation message from ${this._versionBroadcastChannel.name},
        current version is ${this._clientVersion} new version is ${payload.workerVersion}`);
      this.refreshVersionedSharedWorkerClient(payload.workerVersion);
    };
    this._resetSharedWorkerConnection = () => {
      if (!this._sharedWorker) return;
      this._sharedWorker.port.postMessage(createUnRegisterPortConnectionMessage({
        originConnectionId: this._connectionId,
        name: this._name,
        appDataVersion: this._appDataVersion
      }));
      this._sharedWorker.port.close();
      this._sharedWorker = null;
    };
    this._resetBroadcastMessageChannels = (resetVersionBroadcastChannel = false) => {
      if (resetVersionBroadcastChannel && this._versionBroadcastChannel) {
        this._versionBroadcastChannel.close();
        this._versionBroadcastChannel = null;
      }
      if (this._broadcastChannels) {
        this._broadcastChannels.fromWorkerChannel.close();
        this._broadcastChannels.toWorkerChannel.close();
      }
      this._broadcastChannels = null;
      this._outboundBroadcastChannel = null;
    };
    this._onWorkerError = e => {
      this._onMessageError(new Error(GENERIC_WORKER_ERROR), {
        type: e.type
      });
      if (!e.message && this._portConnectedDeferredTimeout) {
        clearTimeout(this._portConnectedDeferredTimeout);
        this._portConnectedDeferredTimeout = undefined;
        this._portConnectedDeferred.reject(new Error('SharedWorker script fetch error'));
        return;
      }
    };
    this.sendMessage = message => {
      if (this._sharedWorker) {
        const workerMessage = createWorkerMessage(message, this._connectionId);
        try {
          if (!this._outboundBroadcastChannel) throw new Error('outboundBroadcastChannel is not set');
          this._outboundBroadcastChannel.postMessage(workerMessage);

          // deprecated
          this._sharedWorker.port.postMessage(workerMessage);
        } catch (e) {
          this._onMessageError(new Error(`SharedWorkerMessagingClient#sendMessage error unable to send ${workerMessage.type}, reason: ${e && e.message}, status: ${this.connectionStatus}`), {
            messageToSend: message
          });
        }
        return;
      }
      this._onMessageError(new Error(`SharedWorkerMessagingClient#sendMessage error unable to send ${message.type}, reason: shared worker does not exist, status: ${this.connectionStatus}`), {
        messageToSend: message
      });
    };
    this.connect = () => new Promise((resolve, reject) => {
      // Don't blow up the world if we can't initialize SharedWorker for some reason
      try {
        const channelName = makeChannelName({
          portalId: this._portalId,
          name: this._name,
          workerVersion: this._clientVersion
        });
        if (!this._versionBroadcastChannel) this._versionBroadcastChannel = createVersionBroadcastChannel({
          channelName: this._name,
          onMessage: this._handleVersionBroadcastChannelMessage,
          onMessageError: this._onMessageError
        });
        if (!this._outboundBroadcastChannel) {
          this._broadcastChannels = createBroadcastMessageChannels({
            portalId: this._portalId,
            channelName: this._name,
            version: this._clientVersion,
            onMessageError: this._onMessageError
          });
          if (!this._broadcastChannels) {
            reject(new Error('BroadcastChannel is unavailable'));
            return;
          }
          this._outboundBroadcastChannel = this._broadcastChannels.toWorkerChannel;
          this._broadcastChannels.fromWorkerChannel.addEventListener('message', this._handleMessage.bind(this, MessageChannelTypes.BROADCAST_MESSAGES));
        }
        this._sharedWorker = connectSharedWorkerPort({
          url: `${this._sharedWorkerUrl}?v=${this._clientVersion}`,
          workerName: channelName,
          onMessage: this._handleMessage.bind(this, MessageChannelTypes.PORT_MESSAGES),
          onMessageError: this._onMessageError,
          onWorkerError: this._onWorkerError,
          version: this._appDataVersion
        });
        this._portConnectedDeferredTimeout = setTimeout(() => {
          this._portConnectedDeferred.reject(new Error('Port Connected Acknowledgement never recieved from SharedWorker script'));
        }, 15000);
        if (this._debug) this._debug(`Opened connection to SharedWorker: ${channelName}`);
        resolve(undefined);
      } catch (e) {
        if (this._debug) this._debug(`Failed to open connection to SharedWorker`, e);
        reject(e);
      }
    });
    this.close = () => {
      clearTimeout(this._portConnectedDeferredTimeout);
      this._portConnectedDeferredTimeout = undefined;
      this._resetBroadcastMessageChannels(true);
      this._resetSharedWorkerConnection();
    };
    this.open = () => {
      let portConnectedError;
      this.connectionStatus = ConnectionStatusTypes.STARTED;
      return this.connect().then(() => this._portConnectedDeferred.promise).catch(e => {
        throw e;
      }).then(this._sendPortRegistration).then(this._handleSharedWorkerInitialized).then(result => {
        this.connectionStatus = ConnectionStatusTypes.SUCCEEDED;
        return result;
      }).catch(e => {
        this._resetBroadcastMessageChannels(true);
        this._resetSharedWorkerConnection();
        if (e && e.message === this.DISCONNECTED_REGISTRATION_ERROR_MESSAGE) {
          throw e;
        }
        this.connectionStatus = ConnectionStatusTypes.FAILED;
        if (portConnectedError) throw portConnectedError;
        throw e;
      });
    };
    this.refreshVersionedSharedWorkerClient = nextWorkerVersion => {
      this._handleConnectionStatusUpdate(ConnectionStatusTypes.STARTED);
      this._portConnectedDeferred = defer();
      this._clientVersion = nextWorkerVersion;
      this._resetSharedWorkerConnection();
      this._resetBroadcastMessageChannels();

      // TODO in this refresh we'll probably need to tell it to not check for an old version again.
      if (this._debug) this._debug(`Refreshing version, new version is ${this._clientVersion}`);
      return this.connect().then(() => this._portConnectedDeferred.promise).then(this._sendPortRegistration).then(this._handleSharedWorkerInitialized).then(data => {
        if (!data) throw new Error('WorkerRefresh failed with no data');
        this._handleConnectionStatusUpdate(ConnectionStatusTypes.SUCCEEDED, data);
      }).catch(e => {
        if (this._debug) this._debug(`WorkerRefresh failed`, e);
        this._handleConnectionStatusUpdate(ConnectionStatusTypes.FAILED);
        this._resetBroadcastMessageChannels(true);
        this._resetSharedWorkerConnection();
      });
    };
    const {
      portalId,
      connectionId,
      initialClientVersion,
      name,
      sharedWorkerUrl,
      onMessage,
      onMessageError,
      onVersionWorkerStatusUpdate,
      getAdditionalRegistrationInformation,
      appDataVersion,
      debug
    } = options;
    this._connectionId = connectionId;
    this._portalId = portalId;
    this._appDataVersion = appDataVersion;
    this._debug = debug;
    this._onMessage = onMessage;
    this._clientVersion = initialClientVersion;
    this._name = name;
    this._sharedWorkerUrl = sharedWorkerUrl;
    this._getAdditionalRegistrationInformation = getAdditionalRegistrationInformation;
    this._onMessageError = onMessageError;
    this._onVersionWorkerStatusUpdate = onVersionWorkerStatusUpdate;
    this._portConnectedDeferred = defer();
  }
  get portConnectedDeferred() {
    return this._portConnectedDeferred;
  }

  /**
   * Currently we just care about recieving a message and not where it came from.
   * Because of how the standalone connection works we need to retain port messaging logic.
   * This method will filter duplicated messages just incase.
   */
}